<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'SLA por Canal',
  },
});

const sortedData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data].sort((a, b) => b.compliance_rate - a.compliance_rate);
});

const avgCompliance = computed(() => {
  if (sortedData.value.length === 0) return 0;
  const total = sortedData.value.reduce((sum, i) => sum + i.compliance_rate, 0);
  return (total / sortedData.value.length).toFixed(1);
});

function getComplianceColor(rate) {
  if (rate >= 90) return 'text-n-teal-11';
  if (rate >= 70) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getBarColor(rate) {
  if (rate >= 90) return 'bg-n-teal-9';
  if (rate >= 70) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function getChannelIcon(channelType) {
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-code',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-shield-check text-lg text-n-teal-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">Conformidade:</span>
        <span
          class="text-lg font-bold"
          :class="getComplianceColor(avgCompliance)"
        >
          {{ avgCompliance }}%
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedData.length === 0"
      class="flex flex-col items-center justify-center h-48 text-n-slate-11"
    >
      <span class="i-lucide-shield-off text-4xl mb-2 opacity-50" />
      <span class="text-sm">Sem dados de SLA</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col gap-3 max-h-72 overflow-y-auto">
      <div
        v-for="(inbox, index) in sortedData"
        :key="inbox.inbox_id"
        class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1"
      >
        <span
          class="text-sm font-medium w-5"
          :class="index < 3 ? 'text-n-teal-11' : 'text-n-slate-10'"
        >
          {{ index + 1 }}
        </span>

        <span
          :class="getChannelIcon(inbox.channel_type)"
          class="text-lg text-n-slate-11"
        />

        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm font-medium text-n-slate-12 truncate">{{
              inbox.inbox_name
            }}</span>
            <span
              class="text-lg font-bold"
              :class="getComplianceColor(inbox.compliance_rate)"
            >
              {{ inbox.compliance_rate }}%
            </span>
          </div>

          <div class="flex items-center gap-2">
            <div class="flex-1 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="getBarColor(inbox.compliance_rate)"
                :style="{ width: `${inbox.compliance_rate}%` }"
              />
            </div>
          </div>

          <div class="flex items-center gap-3 mt-1 text-xs text-n-slate-10">
            <span class="text-n-teal-11">✓ {{ inbox.hit }} no prazo</span>
            <span class="text-n-ruby-11">✗ {{ inbox.missed }} atrasado</span>
            <span class="ml-auto">{{ inbox.total_sla }} total</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
